import { EventTypes, useEditorSelector } from '@candulabs/craft';
import last from 'lodash/last';
import pickBy from 'lodash/pickBy';

import { CRAFT_ROOT_ID, focusableNodes, isRootNode } from 'craft/constants';
import { useEditor } from 'craft/hooks/useEditor';

import { useTraversal } from './useTraversal';

// TODO: remove the hardcoding of focusable nodes

export const useSelection = () => {
  const { query } = useEditor();
  const selection = useEditorSelector((state, q) => {
    // TODO: memoize selector
    const rawEvents = q.getEvent(EventTypes.selected);
    // remove empty node IDs
    const selectedEvents = rawEvents.all().filter((nodeId) => nodeId !== '');

    return {
      isEmpty: selectedEvents.length === 0,
      all: selectedEvents,
      first: selectedEvents[0],
      last: selectedEvents[selectedEvents.length - 1],
      hasRootNode: selectedEvents.includes(CRAFT_ROOT_ID),
    };
  });
  const traversal = useTraversal();

  return {
    selection,
    getDescendants: (nodeId: string) => traversal.descendants(nodeId),

    getSerializedTree: (nodeId: string) => {
      const serializedNodes = query.getSerializedNodes();
      const descendantNodeIds = traversal.descendants(nodeId);
      const descendantNodes = pickBy(
        serializedNodes,
        (value, key) => descendantNodeIds.indexOf(key) >= 0,
      );
      return { rootNodeId: nodeId, nodes: descendantNodes };
    },

    getTree: (nodeId: string) => {
      const descendantNodeIds = traversal.descendants(nodeId);
      const descendantNodes = pickBy(
        query.getState().nodes,
        (value, key) => descendantNodeIds.indexOf(key) >= 0,
      );
      return { rootNodeId: nodeId, nodes: descendantNodes };
    },

    nextFocusNodeId: (nodeId: string) =>
      traversal.nextNodeId(nodeId, { whitelistNodeTypes: focusableNodes }) || null,

    nextNodeId: (nodeId: string) => traversal.nextNodeId(nodeId) ?? null,

    nextSiblingId: (nodeId: string) => {
      if (isRootNode(nodeId)) {
        return null;
      }
      const { nodes } = query.getState();
      const node = nodes[nodeId];

      if (!node) {
        return null;
      }

      const parentNodes = nodes[node.data.parent].data.nodes;
      if (!parentNodes) {
        return null;
      }

      const nodeIndex = parentNodes.indexOf(nodeId);
      const nextSiblingId = parentNodes[nodeIndex + 1];
      return nextSiblingId ?? null;
    },

    nodeIndex: (nodeId: string) => {
      if (isRootNode(nodeId)) {
        return 0;
      }
      const { nodes } = query.getState();
      const node = nodes[nodeId];
      const parentId = node.data.parent;
      return nodes[parentId].data.nodes?.indexOf(nodeId) ?? null;
    },

    previousFocusNodeId: (nodeId: string) =>
      traversal.previousNodeId(nodeId, { whitelistNodeTypes: focusableNodes }) ?? null,

    previousNodeId: (nodeId: string) => traversal.previousNodeId(nodeId) ?? null,

    previousSiblingId: (nodeId: string) => {
      if (isRootNode(nodeId)) {
        return null;
      }
      const { nodes } = query.getState();
      const node = nodes[nodeId];
      const parentNodes = nodes[node.data.parent].data.nodes;
      if (!parentNodes) {
        return null;
      }

      const nodeIndex = parentNodes.indexOf(nodeId);
      const previousSiblingId = parentNodes[nodeIndex - 1];
      return previousSiblingId ?? null;
    },

    lastNodeId: () => last(traversal.descendants(CRAFT_ROOT_ID)) ?? null,
  };
};

export default useSelection;
